import React, { useEffect, useState } from 'react';
import {
    Card, Typography, Row, Col, Button, Divider, Table, DatePicker, Tabs, Tag, Avatar, Progress
} from 'antd';
import {
    ArrowLeftOutlined, PhoneOutlined, MailOutlined, CalendarOutlined,
    EnvironmentOutlined, TrophyOutlined, LineChartOutlined, DollarOutlined,
    TeamOutlined, CheckCircleOutlined, WarningOutlined, AimOutlined,
    DownloadOutlined, ShareAltOutlined, FilterOutlined
} from '@ant-design/icons';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    PieChart, Pie, Cell, ResponsiveContainer,
    Tooltip as RechartsTooltip, LabelList
} from 'recharts';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { EnquiryService } from '../../Services/Manage/EnquiryService';
import { UserService } from '../../Services/Manage/UserService';
import { SalesTargetService } from '../../Services/Manage/SalesTargetService';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import { AddEmployeesService } from '../../Services/Manage/AddEmployeesService';
import { InvoiceService } from '../../Services/Manage/InvoiceService';


dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || {};
    } catch { return {}; }
};

const SalesExecProfile = () => {
    const navigate = useNavigate();
    const { execName: paramName } = useParams();
    const location = useLocation();

    const perm = getPermission('/enquiry');
    const viewAll = perm.view_all === true;

    // Determine whose profile to show
    const getTargetName = () => {
        if (paramName) return decodeURIComponent(paramName);
        if (!viewAll) {
            const user = JSON.parse(localStorage.getItem("ome_user") || "{}");
            return user?.name || user?.user_name || "";
        }
        return "";
    };

    const execName = getTargetName();

    const [allData, setAllData] = useState([]);
    const [wonStatuses, setWonStatuses] = useState([]);
    const [allStatusConfig, setAllStatusConfig] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [targets, setTargets] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [salesExecNames, setSalesExecNames] = useState([]);
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
    const [activeTab, setActiveTab] = useState('overview');

    const [invoices, setInvoices] = useState([]);

    useEffect(() => {
        loadData();
        loadStatuses();
        loadUserInfo();
        loadTargets();
        loadQuotations();
        loadInvoices();
        loadUserMapForRevenue();
        loadSalesExecutives();
    }, []);

    const loadInvoices = async () => {
        try {
            const res = await InvoiceService.getAllInvoice();
            setInvoices(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadQuotations = async () => {
        try {
            const { QuotationService } = await import('../../Services/Manage/QuotationService');
            const res = await QuotationService.getAllQuotation();
            setQuotations(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadUserMapForRevenue = async () => {
        try {
            const res = await UserService.getAllUsers();
            const list = res?.data || res || [];
            const map = {};
            list.forEach((u) => {
                const uid = u.uid || u.user_id || u.id;
                const nm = u.name || u.user_name || u.username;
                if (uid && nm) map[String(uid).trim()] = nm;
            });
            setUserMap(map);
        } catch (e) { console.error(e); }
    };

    const loadSalesExecutives = async () => {
        try {
            const res = await AddEmployeesService.getByDepartment("Sales");
            const names = (res.data || []).map(e => e.name).filter(Boolean);
            setSalesExecNames(names);
        } catch (e) { console.error(e); }
    };

    const loadData = async () => {
        try {
            const res = await EnquiryService.getAllEnquiries();
            setAllData(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadStatuses = async () => {
        try {
            const { ConfigurationService } = await import('../../Services/Manage/ConfigurationService');
            const res = await ConfigurationService.getByType("enquiry_status");
            const all = res.data || [];
            setAllStatusConfig(all);
            setWonStatuses(all.filter(i => i.label.trim().toLowerCase() === "won").map(i => i.label.trim()));
        } catch (e) { console.error(e); }
    };

    const loadUserInfo = async () => {
        try {
            const res = await UserService.getAllUsers();
            const list = res?.data || res || [];
            const found = list.find(u => u.name === execName || u.user_name === execName);
            setUserInfo(found || null);
        } catch (e) { console.error(e); }
    };

    const loadTargets = async () => {
        try {
            const res = await SalesTargetService.getAll();
            setTargets(res.data || []);
        } catch (e) { console.error(e); }
    };

    const parseItems = (items) => {
        if (Array.isArray(items)) return items;
        try { return JSON.parse(items || "[]"); } catch { return []; }
    };

    const amountOf = (d) => {
        let amt = parseFloat(String(d.total_amount ?? 0).replace(/[^0-9.-]/g, "")) || 0;
        if (!amt) {
            const s = parseItems(d.service_items);
            const p = parseItems(d.product_items);
            amt = [...s, ...p].reduce((a, it) => a + (Number(it.unit_price) || 0), 0);
        }
        return amt;
    };
    const isInRange = (d) => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
        const date = dayjs(d.eq_date);
        return date.isValid() && date.isSameOrAfter(dateRange[0], 'day') && date.isSameOrBefore(dateRange[1], 'day');
    };

    const isMine = (d) => {
        const assignedNames = (d.assigned_to || "").split(',').map(s => s.trim()).filter(Boolean);
        const isAssigned = assignedNames.includes(execName);
        const isCreator = assignedNames.length === 0 &&
            salesExecNames.includes(execName) &&
            userMap[String(d.addedby ?? "").trim()] === execName;
        return isAssigned || isCreator;
    };
    // Rows assigned to this exec, or created by them (if they're a sales exec), in date range
    const myRows = allData.filter(d => isMine(d) && isInRange(d));

    const getPrevRows = () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return [];
        const days = dateRange[1].diff(dateRange[0], 'day') + 1;
        const prevEnd = dateRange[0].subtract(1, 'day');
        const prevStart = prevEnd.subtract(days - 1, 'day');
        return allData.filter(d => {
            if (!isMine(d)) return false;
            const date = dayjs(d.eq_date);
            return date.isValid() && date.isSameOrAfter(prevStart, 'day') && date.isSameOrBefore(prevEnd, 'day');
        });
    };
    const prevRows = getPrevRows();

    const trendPct = (curr, prev) => {
        if (prev === 0) return curr > 0 ? { val: null, label: 'New', color: '#52c41a' } : null;
        const pct = Number((((curr - prev) / prev) * 100).toFixed(1));
        return { val: pct, label: `${pct > 0 ? '↑' : '↓'} ${Math.abs(pct)}% vs previous period`, color: pct >= 0 ? '#52c41a' : '#f5222d' };
    };

    // Key metrics
    const totalLeads = myRows.length;
    const prevLeads = prevRows.length;
    const quotationsSent = myRows.filter(d => (d.eq_status || "").toLowerCase().includes("quotation")).length;
    const prevQuotations = prevRows.filter(d => (d.eq_status || "").toLowerCase().includes("quotation")).length;

    // Won deals = quotations CONVERTED for this exec (date-scoped). Revenue = actual
    // COLLECTED amount from each converted quotation's linked invoice payments.
    const collectedOf = (inv) => (inv.payments || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0);
    const invoicesForQuotation = (quNo) =>
        invoices.filter((inv) =>
            inv.qu_no === quNo ||
            inv.source_qu_no === quNo ||
            inv.reference === quNo
        );

    const convertedForExecInRange = (rangeStart, rangeEnd) => {
        return quotations
            .filter((q) => (q.quotation_status || '').toLowerCase() === 'converted')
            .filter((q) => {
                const names = (q.assigned_to || '').split(',').map(s => s.trim()).filter(Boolean);
                if (names.includes(execName)) return true;
                if (names.length === 0 && q.added_by) {
                    const resolvedName = userMap[String(q.added_by).trim()];
                    return resolvedName === execName;
                }
                return false;
            })
            .filter((q) => {
                if (!rangeStart || !rangeEnd) return true;
                const dateStr = q.updated_on || q.date || q.added_on;
                if (!dateStr) return true;
                const d = dayjs(dateStr);
                return d.isValid() && d.isSameOrAfter(rangeStart, 'day') && d.isSameOrBefore(rangeEnd, 'day');
            });
    };

    const collectedForQuotations = (qList) =>
        qList.reduce((sum, q) => {
            const linkedInvoices = invoicesForQuotation(q.qu_no);
            return sum + linkedInvoices.reduce((s, inv) => s + collectedOf(inv), 0);
        }, 0);

    const wonDeals = convertedForExecInRange(dateRange?.[0], dateRange?.[1]).length;
    const prevWon = (() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return 0;
        const days = dateRange[1].diff(dateRange[0], 'day') + 1;
        const prevEnd = dateRange[0].subtract(1, 'day');
        const prevStart = prevEnd.subtract(days - 1, 'day');
        return convertedForExecInRange(prevStart, prevEnd).length;
    })();

    const directRevenue = invoices
        .filter((inv) => {
            const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
            if (names.includes(execName)) return true;
            if (names.length === 0 && inv.added_by) {
                return userMap[String(inv.added_by).trim()] === execName;
            }
            return false;
        })
        .filter((inv) => {
            if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
            const d = dayjs(inv.invoice_date || inv.added_on);
            return d.isValid() && d.isSameOrAfter(dateRange[0], 'day') && d.isSameOrBefore(dateRange[1], 'day');
        })
        .reduce((sum, inv) => sum + collectedOf(inv), 0);

    const totalRevenue = directRevenue || collectedForQuotations(convertedForExecInRange(dateRange?.[0], dateRange?.[1]));
    const prevRevenue = (() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return 0;
        const days = dateRange[1].diff(dateRange[0], 'day') + 1;
        const prevEnd = dateRange[0].subtract(1, 'day');
        const prevStart = prevEnd.subtract(days - 1, 'day');
        return invoices
            .filter((inv) => {
                const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
                if (names.includes(execName)) return true;
                if (names.length === 0 && inv.added_by) {
                    return userMap[String(inv.added_by).trim()] === execName;
                }
                return false;
            })
            .filter((inv) => {
                const d = dayjs(inv.invoice_date || inv.added_on);
                return d.isValid() && d.isSameOrAfter(prevStart, 'day') && d.isSameOrBefore(prevEnd, 'day');
            })
            .reduce((sum, inv) => sum + collectedOf(inv), 0);
    })();
    const conversionRate = totalLeads ? ((wonDeals / totalLeads) * 100).toFixed(2) : "0.00";
    const prevConv = prevLeads ? ((prevWon / prevLeads) * 100).toFixed(2) : "0.00";

    const parseFU = (val) => {
        if (!val) return null;
        let d = dayjs(val, "DD-MM-YYYY", true);
        if (!d.isValid()) d = dayjs(val);
        return d.isValid() ? d : null;
    };

    const todayStart = dayjs().startOf('day');
    const todayEnd = dayjs().endOf('day');
    const withFU = allData
        .filter(d => isMine(d))
        .map(d => ({ ...d, _fu: parseFU(d.n_follow_up) }))
        .filter(d => d._fu);

    const followUpsDone = myRows.filter(d => d.n_follow_up).length;
    const prevFollowUpsDone = prevRows.filter(d => d.n_follow_up).length;
    const overdueFollowUps = withFU.filter(d => d._fu.isBefore(todayStart)).length;
    const prevOverdue = 0; // placeholder

    // Only count target rows whose target_month falls inside the selected date range
    const isTargetInRange = (t) => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
        if (!t.target_month) return true; // fallback: include if no month set
        const tm = dayjs(t.target_month, "MMMM YYYY");
        if (!tm.isValid()) return true;
        const monthStart = tm.startOf('month');
        const monthEnd = tm.endOf('month');
        return monthStart.isSameOrBefore(dateRange[1], 'day') && monthEnd.isSameOrAfter(dateRange[0], 'day');
    };

    const targetsInRange = targets.filter(isTargetInRange);

    // Monthly target for this exec (sum of their individual + team-member-split targets), scoped to date range
    const monthlyTarget = targetsInRange.reduce((sum, t) => {
        if (t.sales_executive_name === execName) {
            return sum + Number(t.revenue_target || 0);
        }
        const mt = Array.isArray(t.member_targets) ? t.member_targets
            : (() => { try { return JSON.parse(t.member_targets || "[]"); } catch { return []; } })();
        const memberShare = mt
            .filter(m => m.member_name === execName)
            .reduce((a, m) => a + Number(m.revenue_target || 0), 0);
        return sum + memberShare;
    }, 0);
    const targetAchievement = monthlyTarget > 0 ? Math.min(Math.round((totalRevenue / monthlyTarget) * 100), 100) : 0;

    const allMetricKeys = [
        { key: "lead_target", label: "Lead Target" },
        { key: "followup_target", label: "Follow-up Target" },
        { key: "quotation_target", label: "Quotation Target" },
        { key: "conversion_target", label: "Conversion Target (Won Deals)" },
        { key: "revenue_target", label: "Revenue Target (₹)", money: true },
        { key: "collection_target", label: "Collection Target (₹)", money: true },
        { key: "sales_order_target", label: "Sales Order Target" },
        { key: "demo_meeting_target", label: "Demo / Meeting Target" },
        { key: "email_target", label: "Email Target" },
        { key: "call_target", label: "Call Target" },
        { key: "new_customers_target", label: "New Customers Target" },
        { key: "existing_customers_target", label: "Existing Customers Target" },
    ];

    // Targets for this exec (individual or team-split), scoped to the selected date range
    const myAllTargets = targets.filter((t) => {
        const belongsToExec = t.sales_executive_name === execName || (() => {
            const mt = Array.isArray(t.member_targets) ? t.member_targets
                : (() => { try { return JSON.parse(t.member_targets || "[]"); } catch { return []; } })();
            return mt.some((mm) => mm.member_name === execName);
        })();
        if (!belongsToExec) return false;
        return isTargetInRange(t);
    });

    const execMetrics = allMetricKeys.reduce((acc, m) => {
        acc[m.key] = targetsInRange.reduce((sum, t) => {
            if (t.sales_executive_name === execName) {
                return sum + Number(t[m.key] || 0);
            }
            const mt = Array.isArray(t.member_targets) ? t.member_targets
                : (() => { try { return JSON.parse(t.member_targets || "[]"); } catch { return []; } })();
            const share = mt
                .filter(mm => mm.member_name === execName)
                .reduce((a, mm) => a + Number(mm[m.key] || 0), 0);
            return sum + share;
        }, 0);
        return acc;
    }, {});

    const topStats = [
        { title: 'Total Leads', value: totalLeads, trend: trendPct(totalLeads, prevLeads), color: '#1677ff', bg: '#f0f7ff', icon: <TeamOutlined /> },
        { title: 'Quotations Sent', value: quotationsSent, trend: trendPct(quotationsSent, prevQuotations), color: '#13c2c2', bg: '#f0fffe', icon: <AimOutlined /> },
        { title: 'Won Deals', value: wonDeals, trend: trendPct(wonDeals, prevWon), color: '#52c41a', bg: '#f6ffed', icon: <TrophyOutlined /> },
        { title: 'Total Revenue', value: `₹ ${totalRevenue.toLocaleString('en-IN')}`, trend: trendPct(totalRevenue, prevRevenue), color: '#722ed1', bg: '#f9f0ff', icon: <DollarOutlined /> },
        { title: 'Conversion Rate', value: `${conversionRate}%`, trend: trendPct(parseFloat(conversionRate), parseFloat(prevConv)), color: '#fa8c16', bg: '#fff7e6', icon: <LineChartOutlined /> },
        { title: 'Follow-ups Done', value: followUpsDone, trend: trendPct(followUpsDone, prevFollowUpsDone), color: '#1677ff', bg: '#f0f7ff', icon: <CalendarOutlined /> },
        { title: 'Overdue Follow-ups', value: overdueFollowUps, trend: overdueFollowUps > 0 ? { label: `↓ ${overdueFollowUps} overdue`, color: '#f5222d' } : null, color: '#f5222d', bg: '#fff1f0', icon: <WarningOutlined /> },
        { title: 'Target Achievement', value: `${targetAchievement}%`, isProgress: true, target: monthlyTarget, achieved: totalRevenue, color: '#52c41a', bg: '#f6ffed', icon: <AimOutlined /> },
    ];

    // Lead Pipeline funnel
    const pipeline = allStatusConfig.map((s, i) => {
        const isWonLabel = wonStatuses.includes(s.label.trim());
        return {
            label: s.label,
            // "Won" count comes from wonDeals (converted quotations for this exec),
            // matching the stat cards above, instead of the eq_status label count.
            count: isWonLabel ? wonDeals : myRows.filter(d => d.eq_status?.trim() === s.label.trim()).length,
            color: ['#1677ff', '#13c2c2', '#fa8c16', '#722ed1', '#eb2f96', '#52c41a', '#f5222d'][i % 7],
        };
    });
    const pipelineTotal = pipeline.reduce((a, b) => a + b.count, 0);

    const buildWeeklyTrend = () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return [];
        const start = dateRange[0];
        const end = dateRange[1];
        const weeks = [];
        let cur = start.startOf('week');
        while (cur.isSameOrBefore(end, 'day')) {
            const wStart = cur;
            const wEnd = cur.endOf('week').isSameOrBefore(end) ? cur.endOf('week') : end;
            const label = wStart.format('DD MMM');
            const rev = invoices
                .filter((inv) => {
                    const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
                    if (names.includes(execName)) return true;
                    if (names.length === 0 && inv.added_by) return userMap[String(inv.added_by).trim()] === execName;
                    return false;
                })
                .filter((inv) => {
                    const d = dayjs(inv.invoice_date || inv.added_on);
                    return d.isValid() && d.isSameOrAfter(wStart, 'day') && d.isSameOrBefore(wEnd, 'day');
                })
                .reduce((sum, inv) => sum + collectedOf(inv), 0);
            weeks.push({ label, revenue: rev });
            cur = cur.add(1, 'week');
        }
        return weeks;
    };

    const weeklyTrend = buildWeeklyTrend();

    // Lead Source
    const sourceColors = ['#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#13c2c2', '#bfbfbf'];
    const sourceCounts = {};
    myRows.forEach(d => { const s = d.eq_source?.trim() || 'Others'; sourceCounts[s] = (sourceCounts[s] || 0) + 1; });
    const sourceTotal = Object.values(sourceCounts).reduce((a, b) => a + b, 0);
    const sourceData = Object.entries(sourceCounts).map(([name, count], i) => ({
        name, count, pct: sourceTotal ? Math.round((count / sourceTotal) * 100) : 0, color: sourceColors[i % sourceColors.length],
    })).sort((a, b) => b.count - a.count);

    // Today's follow-ups
    const todayFUList = withFU
        .filter(d => d._fu.isBetween(todayStart, todayEnd, null, '[]'))
        .sort((a, b) => a._fu.valueOf() - b._fu.valueOf())
        .slice(0, 5)
        .map(d => {
            const services = parseItems(d.service_items);
            return {
                eq_id: d.eq_id,
                company: d.name || '-',
                service: services.length ? services[services.length - 1].service_name : (d.service_name || '-'),
                time: d._fu.format('hh:mm A'),
                status: d.eq_status || '-',
                mobile: d.mobile_no || d.contact_whatsapp || '',
                type: d.follow_up_type || 'Call',
            };
        });

    // Recent Won Deals (sourced from converted quotations; value = actually collected amount)
    // Recent Won Deals — value is the deal/quoted size (what the customer agreed
    // to), matching "Top Won Deals" elsewhere, since this lists wins not collections.
    const recentWon = convertedForExecInRange(dateRange?.[0], dateRange?.[1])
        .map(q => {
            const lineItems = Array.isArray(q.line_items) ? q.line_items : [];
            return {
                eq_id: q.qu_no,
                deal: lineItems.length ? lineItems[lineItems.length - 1].service_name : '-',
                customer: q.customer_name || '-',
                value: Number(q.grand_total_saved || q.grand_total || 0),
                date: q.updated_on || q.date,
            };
        })
        .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
        .slice(0, 5);
    // Recent Quotations
    const recentQuotations = myRows
        .filter(d => (d.eq_status || "").toLowerCase().includes("quotation"))
        .map(d => ({
            eq_id: d.eq_id,
            customer: d.name || '-',
            value: amountOf(d),
            date: d.enquiry_updated_on || d.eq_date,
            status: d.eq_status || '-',
        }))
        .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
        .slice(0, 5);

    // Lost Deals Analysis
    const lostRows = myRows.filter(d => (d.eq_status || "").toLowerCase().includes("lost"));
    const lostReasonColors = ['#f5222d', '#fa8c16', '#722ed1', '#13c2c2', '#bfbfbf'];
    const lostReasonCounts = {};
    lostRows.forEach(d => {
        const reason = d.lost_reason?.trim() || d.note?.trim() || "Other Reasons";
        lostReasonCounts[reason] = (lostReasonCounts[reason] || 0) + 1;
    });
    const lostTotal = Object.values(lostReasonCounts).reduce((a, b) => a + b, 0);
    const lostData = Object.entries(lostReasonCounts).map(([name, count], i) => ({
        name, count, pct: lostTotal ? Math.round((count / lostTotal) * 100) : 0,
        color: lostReasonColors[i % lostReasonColors.length],
    })).sort((a, b) => b.count - a.count);

    // Upcoming follow-ups
    const upcomingFU = withFU
        .filter(d => d._fu.isAfter(todayEnd))
        .sort((a, b) => a._fu.valueOf() - b._fu.valueOf())
        .slice(0, 5)
        .map(d => ({
            eq_id: d.eq_id,
            company: d.name || '-',
            date: d._fu.format('DD MMM YYYY'),
            time: d._fu.format('hh:mm A'),
            mobile: d.mobile_no || d.contact_whatsapp || '',
            type: d.follow_up_type || 'Call',
        }));

    // Activity timeline (recent events)
    const activityRows = [];
    myRows.forEach(d => {
        if (d.eq_date) activityRows.push({
            time: dayjs(d.eq_date).valueOf(),
            timeStr: dayjs(d.eq_date).format('hh:mm A'),
            color: '#13c2c2',
            title: 'New Enquiry Created',
            sub: `${d.eq_id} - ${d.name || '-'}`,
        });
        const hist = Array.isArray(d.status_history) ? d.status_history : (() => { try { return JSON.parse(d.status_history || '[]'); } catch { return []; } })();
        hist.forEach(h => {
            if (!h.updated_on) return;
            const st = (h.status || "").trim();
            let c = '#722ed1', t = st || 'Status Updated';
            if (st.toLowerCase().includes('quotation')) { c = '#1677ff'; t = 'Quotation Sent'; }
            else if (wonStatuses.includes(st)) { c = '#52c41a'; t = 'Lead Converted'; }
            else if (st.toLowerCase().includes('follow')) { c = '#fa8c16'; t = 'Follow-up Call'; }
            else if (st.toLowerCase().includes('demo')) { c = '#13c2c2'; t = 'Demo Scheduled'; }
            activityRows.push({
                time: dayjs(h.updated_on).valueOf(),
                timeStr: dayjs(h.updated_on).format('hh:mm A'),
                color: c, title: t,
                sub: `${d.eq_id} - ${d.name || '-'}`,
            });
        });
    });
    const recentActivity = activityRows.sort((a, b) => b.time - a.time).slice(0, 6);

    const StatCard = ({ title, value, trend, color, bg, icon, isProgress, target, achieved }) => (
        <Card bodyStyle={{ padding: '10px 14px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
                    {isProgress && (
                        <Progress
                            percent={targetAchievement}
                            size="small"
                            strokeColor={color}
                            showInfo={false}
                            style={{ marginTop: 4, marginBottom: 0 }}
                        />
                    )}
                    {trend && (
                        <div style={{ fontSize: 10, color: trend.color, marginTop: 3 }}>{trend.label}</div>
                    )}
                    {isProgress && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                            of ₹ {target.toLocaleString('en-IN')}
                        </div>
                    )}
                </div>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color, flexShrink: 0 }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const initials = execName ? execName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            {/* Header */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(viewAll ? '/enquiry/sales-performance' : '/enquiry')} />
                    <div>
                        <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>
                            Sales Executive Report - {execName}
                        </Typography.Title>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Dashboard &gt; Reports &gt; Sales Executive Performance &gt; {execName}
                        </Typography.Text>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DatePicker.RangePicker
                        value={dateRange}
                        onChange={(vals) => setDateRange(vals)}
                        format="DD MMM YYYY"
                        allowClear={false}
                        style={{ borderRadius: 8 }}
                    />
                    <Button icon={<FilterOutlined />}>Filter</Button>
                    <Button icon={<ShareAltOutlined />}>Share</Button>
                    <Button type="primary" icon={<DownloadOutlined />}>Download Report</Button>
                </div>
            </div>

            {/* Profile + Stat Cards Row */}
            <div style={{ padding: '12px 16px 0' }}>
                <Row gutter={[12, 12]} align="stretch">
                    {/* Profile Card */}
                    <Col xs={24} sm={24} md={5}>
                        <Card bodyStyle={{ padding: 14 }} style={{ borderRadius: 10, height: '100%', border: '1px solid #f0f0f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                <Avatar size={56} style={{ background: '#1677ff', fontSize: 20, fontWeight: 700, flexShrink: 0 }}>{initials}</Avatar>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: '#222', lineHeight: 1.2 }}>{execName || '-'}</div>
                                    <div style={{ fontSize: 12, color: '#888' }}>Sales Executive</div>
                                    <Tag color="green" style={{ marginTop: 4, fontSize: 11 }}>Active</Tag>
                                </div>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ fontSize: 12, color: '#555' }}>
                                {userInfo?.mobile_no && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                        <PhoneOutlined style={{ color: '#1677ff' }} />
                                        <span>{userInfo.mobile_no}</span>
                                    </div>
                                )}
                                {(userInfo?.email || userInfo?.user_id) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                        <MailOutlined style={{ color: '#1677ff' }} />
                                        <span style={{ wordBreak: 'break-all' }}>{userInfo?.email || userInfo?.user_id}</span>
                                    </div>
                                )}
                                {userInfo?.date_of_join && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                        <CalendarOutlined style={{ color: '#1677ff' }} />
                                        <span>Joined on {dayjs(userInfo.date_of_join).format('DD MMM YYYY')}</span>
                                    </div>
                                )}
                                {(userInfo?.city || userInfo?.location) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <EnvironmentOutlined style={{ color: '#1677ff' }} />
                                        <span>{userInfo?.city || userInfo?.location}</span>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>

                    {/* Stat Cards */}
                    <Col xs={24} sm={24} md={19}>
                        <Row gutter={[8, 8]}>
                            {topStats.map((card, i) => (
                                <Col xs={24} sm={12} md={6} key={i}>
                                    <StatCard {...card} />
                                </Col>
                            ))}
                        </Row>
                    </Col>
                </Row>
            </div>

            {/* Tabs */}
            <div style={{ padding: '8px 16px 0' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        { key: 'overview', label: 'Overview' },
                        { key: 'leads', label: 'Leads' },
                        { key: 'followups', label: 'Follow-ups' },
                        { key: 'quotations', label: 'Quotations' },
                        { key: 'revenue', label: 'Revenue' },
                        { key: 'activities', label: 'Activities' },
                    ]}
                />
            </div>

            {/* Overview Tab Content */}
            {activeTab === 'overview' && (
                <div style={{ padding: '0 16px 8px' }}>
                    {/* Lead Pipeline + Revenue Trend + Lead Source */}
                    <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
                        {/* Lead Pipeline (Funnel) */}
                        <Col xs={24} lg={7}>
                            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                                <Typography.Text strong>Lead Pipeline</Typography.Text>
                                <div style={{ marginTop: 10 }}>
                                    {(() => {
                                        const stages = pipeline.filter(p => p.count > 0);
                                        if (stages.length === 0) {
                                            return <Typography.Text type="secondary" style={{ fontSize: 12 }}>No pipeline data</Typography.Text>;
                                        }
                                        const maxW = 92, minW = 30, n = stages.length;
                                        return stages.map((p, i) => {
                                            const topPct = n > 1 ? maxW - ((maxW - minW) * (i / n)) : maxW;
                                            const botPct = n > 1 ? maxW - ((maxW - minW) * ((i + 1) / n)) : maxW;
                                            const botRel = (botPct / topPct) * 100;
                                            return (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 1 }}>
                                                    {/* Funnel slice (empty) */}
                                                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                                        <div style={{
                                                            width: `${topPct}%`,
                                                            height: 22,
                                                            background: p.color,
                                                            clipPath: `polygon(0 0, 100% 0, ${50 + botRel / 2}% 100%, ${50 - botRel / 2}% 100%)`,
                                                        }} />
                                                    </div>
                                                    {/* Label (right) */}
                                                    <span style={{ fontSize: 11, color: '#555', minWidth: 80 }}>{p.label}</span>
                                                    {/* Count (far right) */}
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: p.color, minWidth: 22, textAlign: 'right' }}>{p.count}</span>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#888' }}>Total Leads</span>
                                    <span style={{ fontWeight: 700, color: '#1677ff' }}>{pipelineTotal}</span>
                                </div>
                            </Card>
                        </Col>

                        {/* Revenue Trend */}
                        <Col xs={24} lg={10}>
                            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <Typography.Text strong>Revenue Trend</Typography.Text>
                                    <Tag style={{ fontSize: 11 }}>Weekly</Tag>
                                </div>
                                {weeklyTrend.length === 0 || weeklyTrend.every(w => w.revenue === 0) ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, color: '#bbb' }}>
                                        <span style={{ fontSize: 28 }}>📈</span>
                                        <span style={{ fontSize: 12, marginTop: 6 }}>No revenue data yet</span>
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={160}>
                                        <AreaChart data={weeklyTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#1677ff" stopOpacity={0.15} />
                                                    <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                                            <RechartsTooltip formatter={v => `₹ ${Number(v).toLocaleString('en-IN')}`} contentStyle={{ fontSize: 11 }} />
                                            <Area type="monotone" dataKey="revenue" stroke="#1677ff" strokeWidth={2.5} fill="url(#revGrad)" dot={{ r: 4, fill: '#1677ff' }} activeDot={{ r: 6 }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )}
                                <Divider style={{ margin: '6px 0 4px' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#888' }}>Total Revenue</span>
                                    <span style={{ fontWeight: 700, color: '#52c41a' }}>₹ {totalRevenue.toLocaleString('en-IN')}</span>
                                </div>
                            </Card>
                        </Col>

                        {/* Lead Source */}
                        <Col xs={24} lg={7}>
                            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                                <Typography.Text strong>Lead Source Performance</Typography.Text>
                                {sourceData.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, color: '#bbb' }}>
                                        <span style={{ fontSize: 28 }}>📊</span>
                                        <span style={{ fontSize: 12, marginTop: 6 }}>No source data yet</span>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                        <div style={{ width: 100, height: 100, flexShrink: 0, position: 'relative' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <RechartsTooltip formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, n]} contentStyle={{ fontSize: 11 }} />
                                                    <Pie data={sourceData} dataKey="count" nameKey="name" innerRadius={28} outerRadius={48} paddingAngle={2}>
                                                        {sourceData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                    </Pie>
                                                </PieChart>
                                            </ResponsiveContainer>
                                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                                                <div style={{ fontSize: 14, fontWeight: 700 }}>{sourceTotal}</div>
                                                <div style={{ fontSize: 9, color: '#888' }}>Total</div>
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {sourceData.map((e, i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <span style={{ color: e.color }}>●</span>
                                                        <span style={{ color: '#333' }}>{e.name}</span>
                                                    </span>
                                                    <span style={{ fontWeight: 700 }}>{e.count} ({e.pct}%)</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {sourceData.length > 0 && (
                                    <>
                                        <Divider style={{ margin: '6px 0 4px' }} />
                                        <div style={{ fontSize: 11, color: '#888' }}>
                                            Top Source: <span style={{ fontWeight: 700, color: '#1677ff' }}>{sourceData[0]?.name} ({sourceData[0]?.pct}%)</span>
                                        </div>
                                    </>
                                )}
                            </Card>
                        </Col>
                    </Row>


                </div>
            )}

            {/* Leads Tab */}
            {activeTab === 'leads' && (
                <div style={{ padding: '0 16px 8px' }}>
                    <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                        <Typography.Text strong>All Leads</Typography.Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <Table size="small" rowKey="eq_id" dataSource={myRows} scroll={{ x: 'max-content' }}
                            columns={[
                                { title: 'Enquiry ID', dataIndex: 'eq_id', render: v => <span style={{ color: '#1677ff', fontWeight: 600 }}>{v}</span> },
                                { title: 'Customer', dataIndex: 'name', render: v => v || '-' },
                                { title: 'Source', dataIndex: 'eq_source', render: v => v || '-' },
                                { title: 'Status', dataIndex: 'eq_status', render: v => <Tag>{v || '-'}</Tag> },
                                { title: 'Value', key: 'value', render: (_, r) => `₹ ${amountOf(r).toLocaleString('en-IN')}` },
                                { title: 'Date', dataIndex: 'eq_date', render: v => v ? dayjs(v).format('DD-MMM-YYYY') : '-' },
                            ]} />
                    </Card>
                </div>
            )}
            {activeTab === 'followups' && (
                <div style={{ padding: '0 16px 8px' }}>
                    <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                        <Typography.Text strong>All Follow-ups</Typography.Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <Table size="small" rowKey="eq_id" dataSource={withFU} scroll={{ x: 'max-content' }}
                            columns={[
                                { title: 'Enquiry ID', dataIndex: 'eq_id', render: v => <span style={{ color: '#1677ff', fontWeight: 600 }}>{v}</span> },
                                { title: 'Customer', dataIndex: 'name', render: v => v || '-' },
                                { title: 'Follow-up Date', dataIndex: 'n_follow_up', render: (v, r) => { const d = r._fu; if (!d) return '-'; const isOver = d.isBefore(todayStart); const isToday = d.isSame(dayjs(), 'day'); return <span style={{ color: isOver ? '#f5222d' : isToday ? '#52c41a' : '#333', fontWeight: isOver || isToday ? 600 : 400 }}>{d.format('DD-MMM-YYYY')}</span>; } },
                                { title: 'Status', dataIndex: 'eq_status', render: v => <Tag>{v || '-'}</Tag> },
                                { title: 'Mobile', dataIndex: 'mobile_no', render: v => v ? <a href={`tel:${v}`}>{v}</a> : '-' },
                            ]} />
                    </Card>
                </div>
            )}
            {activeTab === 'quotations' && (
                <div style={{ padding: '0 16px 8px' }}>
                    <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                        <Typography.Text strong>All Quotations</Typography.Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <Table size="small" rowKey="eq_id" dataSource={myRows.filter(d => (d.eq_status || "").toLowerCase().includes("quotation"))} scroll={{ x: 'max-content' }}
                            columns={[
                                { title: 'Enquiry ID', dataIndex: 'eq_id', render: v => <span style={{ color: '#1677ff', fontWeight: 600 }}>{v}</span> },
                                { title: 'Customer', dataIndex: 'name', render: v => v || '-' },
                                { title: 'Value', key: 'value', render: (_, r) => `₹ ${amountOf(r).toLocaleString('en-IN')}` },
                                { title: 'Date', dataIndex: 'enquiry_updated_on', render: v => v ? dayjs(v).format('DD-MMM-YYYY') : '-' },
                                { title: 'Status', dataIndex: 'eq_status', render: v => <Tag color="blue">{v || '-'}</Tag> },
                            ]} />
                    </Card>
                </div>
            )}
            {activeTab === 'revenue' && (
                <div style={{ padding: '0 16px 8px' }}>
                    <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                        <Typography.Text strong>Converted Quotations Revenue</Typography.Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <Table size="small" rowKey="eq_id" dataSource={convertedForExecInRange(dateRange?.[0], dateRange?.[1]).map(q => {
                            const linked = invoices.filter(inv =>
                                inv.qu_no === q.qu_no ||
                                inv.source_qu_no === q.qu_no ||
                                inv.reference === q.qu_no
                            );
                            const val = linked.length > 0
                                ? linked.reduce((s, inv) => s + collectedOf(inv), 0)
                                : invoices
                                    .filter(inv => {
                                        const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
                                        return names.includes(execName) || (names.length === 0 && userMap[String(inv.added_by || "").trim()] === execName);
                                    })
                                    .filter(inv => inv.qu_no === q.qu_no)
                                    .reduce((s, inv) => s + collectedOf(inv), 0);
                            return {
                                eq_id: q.qu_no,
                                name: q.customer_name,
                                won_date: q.updated_on || q.date,
                                value: val,
                            };
                        })} scroll={{ x: 'max-content' }}
                            summary={() => (<Table.Summary.Row><Table.Summary.Cell index={0} colSpan={3}><b>Total</b></Table.Summary.Cell><Table.Summary.Cell index={3} align="right"><b style={{ color: '#722ed1' }}>₹ {totalRevenue.toLocaleString('en-IN')}</b></Table.Summary.Cell></Table.Summary.Row>)}
                            columns={[
                                { title: 'Quotation No', dataIndex: 'eq_id', render: v => <span style={{ color: '#1677ff', fontWeight: 600 }}>{v}</span> },
                                { title: 'Customer', dataIndex: 'name', render: v => v || '-' },
                                { title: 'Won Date', dataIndex: 'won_date', render: v => v ? dayjs(v).format('DD-MMM-YYYY') : '-' },
                                { title: 'Revenue', dataIndex: 'value', align: 'right', render: v => <span style={{ fontWeight: 700, color: '#722ed1' }}>₹ {Number(v).toLocaleString('en-IN')}</span> },
                            ]} />
                    </Card>
                </div>
            )}
            {activeTab === 'activities' && (
                <div style={{ padding: '0 16px 8px' }}>
                    <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                        <Typography.Text strong>Activity Log</Typography.Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <div>
                            {activityRows.sort((a, b) => b.time - a.time).slice(0, 20).map((a, i, arr) => (
                                <div key={i} style={{ display: 'flex', gap: 12, position: 'relative', paddingBottom: i < arr.length - 1 ? 8 : 0 }}>
                                    <div style={{ minWidth: 110, flexShrink: 0, paddingTop: 1 }}>
                                        <div style={{ fontSize: 11, color: '#1677ff', fontWeight: 600 }}>
                                            {dayjs(a.time).isSame(dayjs(), 'day') ? 'Today' : dayjs(a.time).isSame(dayjs().subtract(1, 'day'), 'day') ? 'Yesterday' : dayjs(a.time).format('DD MMM YYYY')}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#999' }}>{a.timeStr}</div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: a.color, marginTop: 3 }} />
                                        {i < arr.length - 1 && <span style={{ width: 2, flex: 1, background: '#f0f0f0', marginTop: 2 }} />}
                                    </div>
                                    <div style={{ flex: 1, paddingBottom: 8 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#222' }}>{a.title}</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>{a.sub}</div>
                                    </div>
                                </div>
                            ))}
                            {activityRows.length === 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>No activities found</Typography.Text>}
                        </div>
                    </Card>
                </div>
            )}

            {/* Always-visible bottom section */}
            <div style={{ padding: '0 16px 16px' }}>
                {/* Bottom Row - always visible */}
                <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
                    {/* Today's Follow-ups */}
                    <Col xs={24} lg={6}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography.Text strong>Today's Follow-ups ({todayFUList.length})</Typography.Text>
                                <Button type="link" size="small" style={{ fontSize: 11, padding: 0 }}>View All</Button>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                {todayFUList.length === 0 ? (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>No follow-ups today</Typography.Text>
                                ) : todayFUList.map((f, i) => (
                                    <div key={f.eq_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: i < todayFUList.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#1677ff', fontWeight: 700, flexShrink: 0 }}>
                                            {(f.company || '?')[0].toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.company}</div>
                                            <div style={{ fontSize: 11, color: '#888' }}>{f.service}</div>
                                        </div>
                                        <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 52 }}>
                                            <div style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>{f.time}</div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 58 }}>
                                            {f.mobile ? (
                                                <a href={`tel:${f.mobile}`} style={{ fontSize: 11, color: '#fff', background: f.type === 'Call' ? '#1677ff' : '#52c41a', padding: '2px 8px', borderRadius: 4, fontWeight: 500 }}>
                                                    {f.type === 'Call' ? 'Call' : 'WhatsApp'}
                                                </a>
                                            ) : <Tag style={{ fontSize: 10 }}>{f.type || 'Call'}</Tag>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Divider style={{ margin: '8px 0 4px' }} />
                            <div style={{ fontSize: 11, color: '#888' }}>Total Follow-ups Today: {todayFUList.length}</div>
                        </Card>
                    </Col>
                    {/* Recent Won Deals */}
                    <Col xs={24} lg={6}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography.Text strong>Recent Won Deals</Typography.Text>
                                <Button type="link" size="small" style={{ fontSize: 11, padding: 0 }}>View All</Button>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                {recentWon.length === 0 ? (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>No won deals yet</Typography.Text>
                                ) : recentWon.map((w, i) => (
                                    <div key={w.eq_id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 0', borderBottom: i < recentWon.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.customer}</div>
                                            <div style={{ fontSize: 11, color: '#888' }}>{w.deal}</div>
                                        </div>
                                        <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 72 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#722ed1' }}>₹{w.value.toLocaleString('en-IN')}</div>
                                            <div style={{ fontSize: 10, color: '#999' }}>{w.date ? dayjs(w.date).format('DD-MMM-YY') : '-'}</div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 36 }}>
                                            <Tag color="green" style={{ fontSize: 10, margin: 0 }}>Won</Tag>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Divider style={{ margin: '8px 0 4px' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                                <span>Total Won Deals: {wonDeals}</span>
                                <span style={{ color: '#722ed1', fontWeight: 700 }}>Total Value: ₹{totalRevenue.toLocaleString('en-IN')}</span>
                            </div>
                        </Card>
                    </Col>
                    {/* Recent Quotations */}
                    <Col xs={24} lg={6}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography.Text strong>Recent Quotations</Typography.Text>
                                <Button type="link" size="small" style={{ fontSize: 11, padding: 0 }}>View All</Button>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                {recentQuotations.length === 0 ? (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>No quotations yet</Typography.Text>
                                ) : recentQuotations.map((q, i) => (
                                    <div key={q.eq_id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 0', borderBottom: i < recentQuotations.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1677ff' }}>{q.eq_id}</div>
                                            <div style={{ fontSize: 11, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.customer}</div>
                                        </div>
                                        <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 72 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#722ed1' }}>₹{q.value.toLocaleString('en-IN')}</div>
                                            <div style={{ fontSize: 10, color: '#999' }}>{q.date ? dayjs(q.date).format('DD-MMM-YY') : '-'}</div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 46 }}>
                                            <Tag color={q.status?.toLowerCase().includes('pending') ? 'orange' : 'blue'} style={{ fontSize: 10, margin: 0 }}>
                                                {q.status?.toLowerCase().includes('pending') ? 'Pending' : 'Sent'}
                                            </Tag>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Divider style={{ margin: '8px 0 4px' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                                <span>Total Quotations: {quotationsSent}</span>
                                <span style={{ color: '#1677ff', fontWeight: 700 }}>Total Value: ₹{recentQuotations.reduce((s, q) => s + q.value, 0).toLocaleString('en-IN')}</span>
                            </div>
                        </Card>
                    </Col>
                    {/* Lost Deals Analysis */}
                    <Col xs={24} lg={6}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography.Text strong>Lost Deals Analysis</Typography.Text>
                                <Button type="link" size="small" style={{ fontSize: 11, padding: 0 }}>View All</Button>
                            </div>
                            {lostData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120, color: '#bbb' }}>
                                    <span style={{ fontSize: 28 }}>🎯</span>
                                    <span style={{ fontSize: 12, marginTop: 6 }}>No lost deals</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                    <div style={{ width: 100, height: 100, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, n]} contentStyle={{ fontSize: 11 }} />
                                                <Pie data={lostData} dataKey="count" nameKey="name" innerRadius={28} outerRadius={48} paddingAngle={2}>
                                                    {lostData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                                            <div style={{ fontSize: 14, fontWeight: 700 }}>{lostTotal}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total Lost</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {lostData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700 }}>{e.count} ({e.pct}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <Divider style={{ margin: '8px 0 4px' }} />
                            <div style={{ fontSize: 11, color: '#888' }}>Total Lost Deals: {lostTotal}</div>
                        </Card>
                    </Col>
                </Row>

                {/* Monthly Target + Activity Timeline + Upcoming - always visible */}
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Monthly Target Tracking</Typography.Text>
                            <Row gutter={[6, 6]} style={{ marginTop: 8 }}>
                                {[
                                    { label: 'Monthly Target', value: `₹ ${monthlyTarget.toLocaleString('en-IN')}`, color: '#222' },
                                    { label: 'Achieved', value: `₹ ${totalRevenue.toLocaleString('en-IN')}`, color: '#52c41a' },
                                    { label: 'Remaining', value: `₹ ${Math.max(monthlyTarget - totalRevenue, 0).toLocaleString('en-IN')}`, color: '#f5222d' },
                                    { label: 'Achievement', value: `${targetAchievement}%`, color: '#1677ff' },
                                ].map((s, i) => (
                                    <Col span={12} key={i}>
                                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px', border: '1px solid #f0f0f0' }}>
                                            <div style={{ fontSize: 11, color: '#888' }}>{s.label}</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.value}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                            <div style={{ marginTop: 8 }}>
                                <Progress percent={targetAchievement} strokeColor={{ '0%': '#1677ff', '100%': '#52c41a' }} style={{ marginBottom: 2 }} />
                                <div style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>
                                    {targetAchievement < 100 ? `You are ${100 - targetAchievement}% away from achieving your monthly target.` : 'Monthly target achieved! 🎉'}
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} lg={10}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography.Text strong>Activity Timeline</Typography.Text>
                                <Button type="link" size="small" style={{ fontSize: 11, padding: 0 }} onClick={() => setActiveTab('activities')}>View All</Button>
                            </div>
                            {recentActivity.length === 0 ? (
                                <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>No recent activities</Typography.Text>
                            ) : (
                                <div style={{ display: 'flex', gap: 0, marginTop: 16, overflowX: 'auto', paddingBottom: 8 }}>
                                    {recentActivity.slice(0, 6).map((a, i) => (
                                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 0 100px', position: 'relative' }}>
                                            {i < recentActivity.slice(0, 6).length - 1 && (
                                                <div style={{ position: 'absolute', top: 8, left: '50%', right: '-50%', height: 2, background: '#e8e8e8', zIndex: 0 }} />
                                            )}
                                            <div style={{ width: 16, height: 16, borderRadius: '50%', background: a.color, zIndex: 1, flexShrink: 0, border: '2px solid #fff', boxShadow: `0 0 0 2px ${a.color}40` }} />
                                            <div style={{ marginTop: 8, textAlign: 'center', padding: '0 4px' }}>
                                                <div style={{ fontSize: 10, color: '#1677ff', fontWeight: 600 }}>
                                                    {dayjs(a.time).isSame(dayjs(), 'day') ? 'Today' : dayjs(a.time).isSame(dayjs().add(1, 'day'), 'day') ? 'Tomorrow' : dayjs(a.time).isSame(dayjs().subtract(1, 'day'), 'day') ? 'Yesterday' : dayjs(a.time).format('DD MMM')}
                                                </div>
                                                <div style={{ fontSize: 10, color: '#999' }}>{a.timeStr}</div>
                                                <div style={{ fontSize: 11, fontWeight: 600, color: '#222', marginTop: 2 }}>{a.title}</div>
                                                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{a.sub}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} lg={6}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography.Text strong>Upcoming Follow-ups</Typography.Text>
                                <Button type="link" size="small" style={{ fontSize: 11, padding: 0 }} onClick={() => setActiveTab('followups')}>View All</Button>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                {upcomingFU.length === 0 ? (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>No upcoming follow-ups</Typography.Text>
                                ) : upcomingFU.map((f, i) => (
                                    <div key={f.eq_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < upcomingFU.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#222' }}>{f.company}</div>
                                            <div style={{ fontSize: 11, color: '#52c41a' }}>{f.date}</div>
                                        </div>
                                        {f.mobile ? (
                                            <a href={`tel:${f.mobile}`} style={{ fontSize: 11, color: '#1677ff', background: '#f0f7ff', padding: '2px 8px', borderRadius: 4 }}>
                                                {f.type === 'Call' ? 'Call' : 'WhatsApp'}
                                            </a>
                                        ) : <Tag style={{ fontSize: 10 }}>{f.type || 'Call'}</Tag>}
                                    </div>
                                ))}
                            </div>
                            <Divider style={{ margin: '8px 0 4px' }} />
                            <div style={{ fontSize: 11, color: '#888' }}>Total Upcoming: {upcomingFU.length}</div>
                        </Card>
                    </Col>
                </Row>

                {/* Target Metrics - per target, full width */}
                <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                    <Col span={24}>
                        <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 14 }}>
                            <Typography.Text strong>Target Metrics (All Assigned Targets)</Typography.Text>
                            <Table
                                size="small"
                                style={{ marginTop: 8 }}
                                rowKey={(t) => t.id}
                                dataSource={myAllTargets}
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                locale={{ emptyText: 'No targets assigned' }}
                                columns={[
                                    {
                                        title: 'Period', key: 'period_label', width: 90,
                                        render: (t) => <Tag>{t.period || '—'}</Tag>,
                                    },
                                    {
                                        title: 'Month / Quarter / Year', dataIndex: 'target_month', key: 'target_month',
                                        render: (v) => {
                                            if (!v) return '—';
                                            const qMatch = v.match(/^Q(\d)\s+(\d{4})$/);
                                            if (qMatch) {
                                                const qLabels = { 1: 'Jan - Mar', 2: 'Apr - Jun', 3: 'Jul - Sep', 4: 'Oct - Dec' };
                                                return `${qLabels[qMatch[1]]} ${qMatch[2]}`;
                                            }
                                            return v;
                                        },
                                    },
                                    {
                                        title: 'Target For', key: 'target_for_label',
                                        render: (t) => t.sales_executive_name === execName
                                            ? 'Individual'
                                            : `Team: ${t.team_name || '—'}`,
                                    },
                                    ...allMetricKeys.map((m) => ({
                                        title: m.label,
                                        key: m.key,
                                        align: 'right',
                                        render: (t) => {
                                            let v;
                                            if (t.sales_executive_name === execName) {
                                                v = Number(t[m.key] || 0);
                                            } else {
                                                const mt = Array.isArray(t.member_targets) ? t.member_targets
                                                    : (() => { try { return JSON.parse(t.member_targets || "[]"); } catch { return []; } })();
                                                v = mt.filter(mm => mm.member_name === execName)
                                                    .reduce((a, mm) => a + Number(mm[m.key] || 0), 0);
                                            }
                                            return m.money ? `₹ ${v.toLocaleString('en-IN')}` : v;
                                        },
                                    })),
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>

        </Card>
    );
};

export default SalesExecProfile;